import React, { useCallback, useEffect, useState } from 'react';
import * as R from 'remeda';
import deepEqual from 'fast-deep-equal/es6/react';
import { connect, useSelector } from 'react-redux';
import {
  RouteComponentProps,
  useHistory,
  useLocation,
  withRouter,
} from 'react-router-dom';
import { Dispatch } from 'redux';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Stack } from '@rossum/ui/material';
import { DataCaptureTourDialog } from '../../components/productTour/DataCaptureTourDialog';
import ValidationKeyboardHandler from '../../decorators/ValidationKeyboardHandler';
import { ValidationDocumentDrawer } from '../../features/annotation-view/document-drawer/ValidationDocumentDrawer';
import { DocumentStore } from '../../features/annotation-view/document-store/DocumentStore';
import {
  DocumentContext,
  DocumentContextProvider,
} from '../../features/annotation-view/DocumentContext';
import { useIsFeatureEnabled } from '../../features/feature-enablement/featureEnablement';
import { DataCaptureSurvey } from '../../features/surveys/DataCaptureSurvey/DataCaptureSurvey';
import { getCurrentAnnotationId, getEditModeUrl, parse } from '../../lib/url';
import { exitAnnotation as exitAnnotationAction } from '../../redux/modules/annotation/actions';
import { currentAnnotationQueueSelector } from '../../redux/modules/annotation/currentAnnotationQueueSelector';
import { annotationSideloadsSelector } from '../../redux/modules/annotation/selectors';
import { closeSelectMenu as closeSelectMenuAction } from '../../redux/modules/ui/actions';
import { ensureArray } from '../../redux/modules/utils';
import { HelmetComponent } from '../../routes/HelmetComponent';
import { ID } from '../../types/basic';
import { State as ReduxState } from '../../types/state';
import { DocumentV2 } from '../Document/DocumentV2';
import { DocumentMetrics } from './DocumentMetrics';
import { DrawerConfig, ValidationEmailDrawer } from './ValidationEmailDrawer';

type StateProps = {
  currentDatapointPath: Array<number>;
  annotationId: number;
  selectMenuIsOpen: boolean;
  documentName: string | undefined;
};

type OwnProps = RouteComponentProps;

type DispatchProps = {
  closeSelectMenu: () => void;
  exitAnnotation: (annotationId: ID) => void;
};

type DocumentValidationProps = StateProps & OwnProps & DispatchProps;

const DocumentValidation = React.memo(
  ({
    selectMenuIsOpen,
    exitAnnotation,
    closeSelectMenu,
    annotationId,
    documentName,
  }: DocumentValidationProps) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerConfig, setDrawerConfig] = useState<DrawerConfig>({});

    const history = useHistory();
    const location = useLocation();

    const isSplitEnabled = useIsFeatureEnabled('mat-1949-splitting-view');

    // Always check the queue to which the annotation belongs
    // TODO: Is this correct?
    const currentQueue = useSelector(currentAnnotationQueueSelector);

    const validationDisabled =
      currentQueue?.settings?.uiValidationScreenEnabled === false;

    useEffect(() => {
      if (validationDisabled) {
        history.replace(
          getEditModeUrl(isSplitEnabled, { id: annotationId }),
          location.state
        );
      }
    }, [
      annotationId,
      history,
      isSplitEnabled,
      location.state,
      validationDisabled,
    ]);

    useEffect(() => {
      const closeSelectMenuSubscription = fromEvent(
        window.document,
        'mousedown'
      )
        .pipe(
          filter(() => selectMenuIsOpen),
          // FIXME: remove this filter when 'react-select' will be removed from project
          filter(({ srcElement }) => {
            const className = (srcElement as Element).getAttribute('class');

            if (!className) {
              return true;
            }

            return !className.includes('react-select');
          }),
          // avoid to close select when user click inside select
          filter(({ srcElement }) => {
            const className = (srcElement as Element).getAttribute('class');

            if (!className) {
              return true;
            }

            return !className.includes('select_box');
          })
        )
        .subscribe(() => closeSelectMenu());

      return () => closeSelectMenuSubscription.unsubscribe();
    }, [selectMenuIsOpen, closeSelectMenu]);

    useEffect(() => {
      return () => {
        const annotationIdFromUrl = getCurrentAnnotationId(
          window.location.pathname
        );
        const stayingOnSameAnnotation =
          annotationIdFromUrl && annotationIdFromUrl === annotationId;
        // When redirecting to the legacy edit mode or to the split route
        // (/split/:id, MAT-1958) the component unmounts, but we stay on the
        // same annotation, so we don't want to exit it.
        if (!stayingOnSameAnnotation) {
          exitAnnotation(annotationId);
        }
      };
    }, [annotationId, exitAnnotation]);

    // Keep properly memoized version for the new sidebar tho :swag:
    const onEmailThreadOpen = useCallback((newDrawerConfig?: DrawerConfig) => {
      setDrawerOpen(true);
      setDrawerConfig(newDrawerConfig || {});
    }, []);

    const handleDrawerClose = useCallback(() => {
      setDrawerOpen(false);
    }, []);

    return (
      <>
        <HelmetComponent
          dynamicName={documentName}
          translationKey="features.routes.pageTitles.document"
        />

        <DocumentContextProvider>
          <DocumentContext.Consumer>
            {documentContextValue =>
              documentContextValue ? (
                <Stack
                  sx={{ height: '100%' }}
                  data-page-title="document-validation"
                >
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      alignContent: 'stretch',
                      minHeight: 0,
                      flexGrow: 1,
                    }}
                  >
                    <DocumentStore key={annotationId}>
                      <ValidationDocumentDrawer />
                      <DocumentMetrics annotationId={annotationId} />
                      <DataCaptureTourDialog />
                      <DocumentV2 onEmailThreadOpen={onEmailThreadOpen} />
                    </DocumentStore>
                  </Stack>
                </Stack>
              ) : null
            }
          </DocumentContext.Consumer>
        </DocumentContextProvider>
        {/* The key is important - re-mounts data capture survey to track stuff from scratch. */}
        <DataCaptureSurvey annotationId={annotationId} key={annotationId} />
        <ValidationEmailDrawer
          DrawerProps={{
            open: drawerOpen,
            onClose: handleDrawerClose,
          }}
          {...drawerConfig}
        />
      </>
    );
  },
  (prevProps, nextProps) => deepEqual(prevProps, nextProps)
);

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    router: {
      location: { pathname },
    },
  } = state;

  const annotationId = getCurrentAnnotationId(pathname);
  const datapointPath = ensureArray(
    parse(state.router.location.search)['datapointPath']
  );
  const documentName =
    annotationSideloadsSelector(state).document?.originalFileName ?? undefined;

  return {
    documentName,
    currentDatapointPath: datapointPath,
    annotationId,
    selectMenuIsOpen: state.ui.selectMenuIsOpen,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  closeSelectMenu: () => dispatch(closeSelectMenuAction()),
  exitAnnotation: (id: number) => dispatch(exitAnnotationAction(id)),
});

export default R.pipe(
  DocumentValidation,
  connect<StateProps, DispatchProps, OwnProps, ReduxState>(
    mapStateToProps,
    mapDispatchToProps
  ),
  ValidationKeyboardHandler,
  withRouter
);
